import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useReactiveVar } from "@apollo/client"
import type { StoreType } from "@beautypie/core"
import type { LDClient } from "launchdarkly-js-client-sdk"
import { initialize } from "launchdarkly-js-client-sdk"

import { isServer } from "@shared/utils.environment"

import { useRegion } from "hooks"
import {
  bpLdId as bpLdIdVar,
  testIdentity as testIdentityVar,
} from "lib/apollo/variables"
import { useUser } from "lib/user"
import { getExperienceId } from "utils/getExperienceId"

type FeatureFlagContextType = {
  ldClient: LDClient | undefined
}

type LDUser = {
  kind: "user"
  key: string
  name?: string
  anonymous: boolean
  email?: string
  custom: {
    locale?: string
    experienceId?: string
    testIdentity?: string
    bpLdId?: string
    store?: StoreType
    initialisedAt?: number
    customerNumber?: number
    membershipSku?: string
  }
}

const LAUNCH_DARKLY_USER_KEY = "anonymous-frontend"

export const createAnonymousLDUser = (
  email: string,
  experienceId: string | undefined,
  testIdentity: string,
  bpLdId: string,
  store: StoreType,
  customerNumber?: number,
  membershipSku?: string
): LDUser => {
  return {
    kind: "user",
    key: LAUNCH_DARKLY_USER_KEY,
    email,
    anonymous: true,
    custom: {
      store,
      experienceId,
      testIdentity,
      bpLdId,
      initialisedAt: Math.round(Date.now() / 1000), // unix timestamp in seconds
      customerNumber,
      membershipSku,
    },
  }
}

export const FeatureFlagContext = createContext({
  ldClient: undefined,
} as FeatureFlagContextType)

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLDReady, setIsLDReady] = useState<boolean>(false)

  const ldClientRef = useRef<LDClient>()

  const user = useUser()
  const store = useRegion()
  const experienceId = getExperienceId()
  const testIdentity = useReactiveVar(testIdentityVar)
  const bpLdId = useReactiveVar(bpLdIdVar)

  const ldUser: LDUser | undefined = useMemo(() => {
    // We don't want to evaluate feature flags when server side rendering
    // As the results of SSR are typically cached
    if (isServer()) {
      return
    }

    // Don't initialise the feature flag context until the user is pulled out
    // of the cache
    if (!user) {
      return
    }

    return createAnonymousLDUser(
      user?.email || "anonymous@test.com",
      experienceId,
      testIdentity,
      bpLdId,
      store,
      Number.isNaN(Number(user?.id)) ? -1 : Number(user?.id),
      user?.membership?.value || ""
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, experienceId, bpLdId, user?.id, user?.membership?.value])

  useEffect(() => {
    // No ldUser available in SSR scenario
    if (!ldUser) {
      return
    }

    if (!process.env.NEXT_PUBLIC_LD_CLIENT_ID) {
      console.error("Invalid configuration for LaunchDarkly")
      return
    }

    if (!isLDReady) {
      const client = initialize(process.env.NEXT_PUBLIC_LD_CLIENT_ID, ldUser)

      client.on("ready", () => {
        ldClientRef.current = client
        setIsLDReady(true)
      })
    } else {
      ldClientRef?.current?.identify(ldUser)
    }
  }, [ldUser, isLDReady])

  useEffect(() => {
    return () => {
      ldClientRef?.current?.close()
    }
  }, [])

  return (
    <FeatureFlagContext.Provider value={{ ldClient: ldClientRef.current }}>
      {children}
    </FeatureFlagContext.Provider>
  )
}
